import { memo, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Badge, Button, fieldClass, selectClass, Sheet, SheetContent, textareaClass } from '@ledgerloom/ui';
import { Field } from './Field';
import { initials, ToggleGroup } from './ui';

// The card board (data-display/kanban-board): the one board any screen or agent
// uses when work moves through stages. Cards move by hand (pointer drag with a
// spring, or Space, arrows, Space on the keyboard), the order is one list, and
// every change comes back whole through onCardsChange.

export interface BoardColumn {
  id: string; title: string;
  /** Work-in-progress limit: the column warns, in words and color, when it holds more. */
  limit?: number;
  /** Cards in the column on the server, when the board shows only some of them (see onLoadMore). */
  total?: number;
  /** A line under the title, such as the column's value. */
  note?: string;
  /** Takes no cards: one already in it can leave. */
  closed?: boolean;
}
/** Everyone a card can be assigned to; it must name every assignee on the cards. */
export interface BoardPerson { id: string; name: string }
export interface ChecklistItem { id: string; text: string; done: boolean }
/** A record field the drawer edits in place; the edit comes back as the card with this field's new value. */
export interface CardField { key: string; label: string; type: 'text' | 'number' | 'date'; value: string }
export interface KanbanCard {
  id: string; title: string; status: string; assignee: string | null;
  subtitle?: string; meta?: string; flags?: string[];
  /** The record's own page, linked from the drawer. */
  href?: string;
  /** A change to it is in flight: it cannot move until it lands. */
  busy?: boolean;
  description?: string;
  checklist?: ChecklistItem[];
  fields?: CardField[];
  /** Why its last change was refused, and the fields that would fix it: the board opens the card and marks them. */
  problem?: { message: string; fields: string[] };
}
export type GroupBy = 'status' | 'assignee';
/** What people may not change on this board. */
export type Fixed = 'add' | 'title' | 'assignee' | 'description';

interface Props {
  columns: BoardColumn[];
  people: BoardPerson[];
  /** The controlled list in board order; omit it and the board owns its list. */
  cards?: KanbanCard[];
  defaultCards?: KanbanCard[];
  /** The full ordered list after every move, edit, checklist toggle, or new card. */
  onCardsChange?: (cards: KanbanCard[]) => void;
  defaultGroupBy?: GroupBy;
  /** The person new cards are assigned to; the first person when left out. */
  currentUser?: string;
  title: string;
  className?: string;
  fixed?: Fixed[];
  /** The words this board uses for a card's status and assignee (Stage, Owner). */
  names?: { status: string; assignee: string };
  onLoadMore?: (columnId: string) => void;
}

interface Spot { status: string; lane: string | null; index: number }
interface Drag { id: string; to: Spot; from: Spot; mode: 'pointer' | 'keyboard'; w: number; h: number }

const inGroup = (c: KanbanCard, s: { status: string; lane: string | null }, lanes: boolean) => c.status === s.status && (!lanes || c.assignee === s.lane);

// The list with one card taken out and put at a spot: its status, its lane's assignee, and its index there.
export function place(list: KanbanCard[], id: string, to: Spot, lanes: boolean): KanbanCard[] {
  const card = list.find((c) => c.id === id);
  if (!card) return list;
  const rest = list.filter((c) => c.id !== id);
  const moved = card.status === to.status && (!lanes || card.assignee === to.lane) ? card : { ...card, status: to.status, ...(lanes ? { assignee: to.lane } : {}) };
  const group = rest.filter((c) => inGroup(c, to, lanes));
  const at = to.index < group.length ? rest.indexOf(group[to.index]!) : group.length ? rest.indexOf(group[group.length - 1]!) + 1 : rest.length;
  rest.splice(at, 0, moved);
  return rest;
}

const spotOf = (list: KanbanCard[], c: KanbanCard, lanes: boolean): Spot =>
  ({ status: c.status, lane: c.assignee, index: list.filter((o) => inGroup(o, { status: c.status, lane: c.assignee }, lanes)).indexOf(c) });

// Cards that are new or differ from before, for a page that turns each change into a proposal.
// Compared by value, leaving out busy and problem, which the page sets itself.
export function changedCards(before: KanbanCard[], after: KanbanCard[]): Array<{ card: KanbanCard; was?: KanbanCard }> {
  const key = ({ busy: _, problem: __, ...c }: KanbanCard) => JSON.stringify(c);
  const old = new Map(before.map((c) => [c.id, c]));
  return after.filter((c) => { const o = old.get(c.id); return !o || key(o) !== key(c); }).map((c) => ({ card: c, was: old.get(c.id) }));
}

// One spring for everything that moves: semi-implicit Euler, mass 1, a little under critical damping.
const STIFF = 380, DAMP = 30;
function spring(x: number, v: number, target: number, dt: number): [number, number] {
  const nv = v + (-STIFF * (x - target) - DAMP * v) * dt;
  return [x + nv * dt, nv];
}
// The same spring from 0 to 1 as a CSS easing, for neighbors gliding to their new places.
const GLIDE: KeyframeAnimationOptions = !CSS.supports('animation-timing-function', 'linear(0, 1)') ? { easing: 'cubic-bezier(.2, .9, .3, 1.1)', duration: 280 } : (() => {
  const pts: string[] = [];
  let x = 0, v = 0;
  while (pts.length < 90 && (Math.abs(1 - x) > 0.001 || Math.abs(v) > 0.01)) { pts.push(x.toFixed(3)); [x, v] = spring(x, v, 1, 1 / 60); }
  pts.push('1');
  return { easing: `linear(${pts.join(', ')})`, duration: (pts.length * 1000) / 60 };
})();
const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
const EDGE = 56; // px from an edge where a drag starts scrolling

export function CardBoard({ columns, people, cards: controlled, defaultCards, onCardsChange, defaultGroupBy = 'status', currentUser, title, className, fixed = [], names = { status: 'Status', assignee: 'Assignee' }, onLoadMore }: Props) {
  const [own, setOwn] = useState<KanbanCard[]>(defaultCards ?? []);
  const cards = controlled ?? own;
  const [groupBy, setGroupBy] = useState<GroupBy>(defaultGroupBy);
  const lanes = groupBy === 'assignee';
  const [drag, setDragState] = useState<Drag | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const uid = useId();
  const scroller = useRef<HTMLDivElement>(null);
  const float = useRef<HTMLDivElement>(null);
  const live = useRef<HTMLDivElement>(null);
  const nameOf = useMemo(() => new Map(people.map((p) => [p.id, p.name])), [people]);
  const colOf = useMemo(() => new Map(columns.map((c) => [c.id, c])), [columns]);
  const open = columns.filter((c) => !c.closed);

  // Lanes come from the committed list, so a lane does not vanish while its last card is in the air.
  const laneIds = useMemo(() => {
    if (!lanes) return [null];
    const used = new Set(cards.map((c) => c.assignee));
    const ids: Array<string | null> = people.filter((p) => used.has(p.id)).map((p) => p.id);
    for (const a of used) if (a && !nameOf.has(a)) ids.push(a);
    return used.has(null) ? [...ids, null] : ids;
  }, [lanes, cards, people, nameOf]);
  const laneName = (l: string | null) => (l === null ? 'Unassigned' : nameOf.get(l) ?? l);

  const shown = useMemo(() => (drag ? place(cards, drag.id, drag.to, lanes) : cards), [cards, drag, lanes]);

  const change = (next: KanbanCard[]) => { if (!controlled) setOwn(next); onCardsChange?.(next); };
  const say = (text: string) => { if (live.current) live.current.textContent = text; };
  const where = (list: KanbanCard[], s: Spot) => {
    const n = list.filter((c) => inGroup(c, s, lanes)).length;
    return `${colOf.get(s.status)?.title ?? s.status}${lanes ? `, ${laneName(s.lane)}` : ''}, position ${Math.min(s.index, n - 1) + 1} of ${n}`;
  };

  // Everything the window listeners and the frame loop read, current on every render.
  const now = useRef({ cards, shown, drag, lanes, laneIds, open });
  now.current = { cards, shown, drag, lanes, laneIds, open };
  // A fast drag can lift and drop between two renders, so listeners read the drag as it is now, not as last rendered.
  const put = (d: Drag | null) => { now.current.drag = d; setDragState(d); };

  function finish(d: Drag, drop: boolean) {
    const { cards: list, lanes: ln } = now.current;
    const card = list.find((c) => c.id === d.id);
    put(null);
    motion.current.landing = false;
    if (!card) return;
    if (!drop) { say(`Move cancelled. ${card.title} is back in ${where(list, d.from)}.`); return; }
    const next = place(list, d.id, d.to, ln);
    say(`Dropped ${card.title} in ${where(next, d.to)}.`);
    const same = d.to.status === d.from.status && (!ln || d.to.lane === d.from.lane) && d.to.index === d.from.index;
    if (!same) change(next);
  }

  // ---- layout glides: every card that changed place springs from where it was (FLIP)
  const rects = useRef(new Map<string, { x: number; y: number }>());
  useLayoutEffect(() => {
    const root = scroller.current;
    if (!root) return;
    const glide = !reducedMotion();
    const next = new Map<string, { x: number; y: number }>();
    // Offsets are from .board (positioned) and ignore transforms and scrolling, so a running glide does not skew them.
    for (const el of root.querySelectorAll<HTMLElement>('[data-card]')) {
      const p = { x: el.offsetLeft, y: el.offsetTop };
      const was = rects.current.get(el.dataset.card!);
      next.set(el.dataset.card!, p);
      if (glide && was && (was.x !== p.x || was.y !== p.y)) {
        el.getAnimations().forEach((a) => a.cancel());
        el.animate([{ transform: `translate(${was.x - p.x}px, ${was.y - p.y}px)` }, { transform: 'none' }], GLIDE);
      }
    }
    rects.current = next;
  }, [shown, lanes]);

  // A refused change opens its card, once per refusal, so the person sees why and what to fill in.
  const problemSeen = useRef('');
  useEffect(() => {
    const c = cards.find((x) => x.problem);
    const key = c ? `${c.id}:${c.problem!.message}` : '';
    if (key && key !== problemSeen.current) setOpenId(c!.id);
    problemSeen.current = key;
  }, [cards]);

  // A keyboard move can put the card in another list; keep focus on it.
  useLayoutEffect(() => {
    if (drag?.mode === 'keyboard') scroller.current?.querySelector<HTMLElement>(`[data-card="${CSS.escape(drag.id)}"]`)?.focus();
  }, [drag]);

  // ---- pointer drag
  const press = useRef<{ id: string; x: number; y: number; el: HTMLElement; timer?: number } | null>(null);
  const motion = useRef({ x: 0, y: 0, vx: 0, vy: 0, t: 0, gx: 0, gy: 0, tilt: 0, vt: 0, frame: 0, landing: false });
  const justDragged = useRef(false);

  function hitTest(px: number, py: number) {
    const d = now.current.drag;
    const root = scroller.current;
    if (!d || !root) return;
    for (const el of root.querySelectorAll<HTMLElement>('[data-drop]')) {
      const r = el.getBoundingClientRect();
      if (px < r.left || px > r.right || py < r.top || py > r.bottom) continue;
      const status = el.dataset.drop!;
      if (colOf.get(status)?.closed && status !== d.from.status) return;
      const lane = now.current.lanes ? (el.dataset.lane || null) : d.to.lane;
      let index = 0;
      for (const c of el.querySelectorAll<HTMLElement>('[data-card]')) {
        if (c.dataset.card === d.id) continue;
        const cr = c.getBoundingClientRect();
        if (py > cr.top + cr.height / 2) index++;
      }
      const to = { status, lane, index };
      if (to.status !== d.to.status || to.lane !== d.to.lane || to.index !== d.to.index) {
        put({ ...d, to });
        say(where(place(now.current.cards, d.id, to, now.current.lanes), to));
      }
      return;
    }
  }

  function frame() {
    const m = motion.current;
    const el = float.current;
    if (!now.current.drag || now.current.drag.mode !== 'pointer' || m.landing) { m.frame = 0; return; }
    const reduce = reducedMotion();
    [m.tilt, m.vt] = reduce ? [0, 0] : spring(m.tilt, m.vt, Math.max(-8, Math.min(8, m.vx / 90)), 1 / 60);
    if (el) el.style.transform = `translate(${m.x - m.gx}px, ${m.y - m.gy}px) rotate(${m.tilt}deg)`;
    // Scroll near an edge: the board sideways, the page up and down.
    const root = scroller.current;
    if (root) {
      const r = root.getBoundingClientRect();
      const by = (d: number) => Math.round(((EDGE - d) / EDGE) * 18);
      if (m.x < r.left + EDGE) root.scrollLeft -= by(m.x - r.left);
      else if (m.x > r.right - EDGE) root.scrollLeft += by(r.right - m.x);
      if (m.y < EDGE) window.scrollBy(0, -by(m.y));
      else if (m.y > innerHeight - EDGE) window.scrollBy(0, by(innerHeight - m.y));
    }
    hitTest(m.x, m.y);
    m.frame = requestAnimationFrame(() => fn.current.frame());
  }

  function lift(id: string, el: HTMLElement, x: number, y: number) {
    const { cards: list, lanes: ln } = now.current;
    const card = list.find((c) => c.id === id);
    if (!card) return;
    const r = el.getBoundingClientRect();
    const from = spotOf(list, card, ln);
    Object.assign(motion.current, { x, y, vx: 0, vy: 0, t: performance.now(), gx: x - r.left, gy: y - r.top, tilt: 0, vt: 0, landing: false });
    justDragged.current = true;
    put({ id, from, to: from, mode: 'pointer', w: r.width, h: r.height });
    say(`Picked up ${card.title}. ${where(list, from)}.`);
    motion.current.frame ||= requestAnimationFrame(() => fn.current.frame());
  }

  // The floating copy is drawn where the card was, at its size, before the first paint, so it never shows at another size.
  useLayoutEffect(() => {
    const d = now.current.drag, m = motion.current;
    if (d?.mode === 'pointer' && float.current) float.current.style.transform = `translate(${m.x - m.gx}px, ${m.y - m.gy}px)`;
  }, [drag?.id, drag?.mode]);

  function land() {
    const d = now.current.drag;
    const m = motion.current;
    if (!d) return;
    const slot = scroller.current?.querySelector<HTMLElement>('[data-drop-slot]')?.getBoundingClientRect();
    if (!slot || reducedMotion() || !float.current) { finish(d, true); return; }
    m.landing = true;
    let x = m.x - m.gx, y = m.y - m.gy, vx = m.vx, vy = m.vy, t = m.tilt, vt = m.vt;
    const step = () => {
      [x, vx] = spring(x, vx, slot.left, 1 / 60);
      [y, vy] = spring(y, vy, slot.top, 1 / 60);
      [t, vt] = spring(t, vt, 0, 1 / 60);
      if (float.current) float.current.style.transform = `translate(${x}px, ${y}px) rotate(${t}deg)`;
      if (Math.abs(x - slot.left) + Math.abs(y - slot.top) < 0.5 && Math.abs(vx) + Math.abs(vy) < 20) { fn.current.finish(d, true); return; }
      requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  // Window listeners and frame loops outlive a render: they call the latest functions through this.
  const fn = useRef({ lift, land, finish, frame, hitTest });
  fn.current = { lift, land, finish, frame, hitTest };

  useEffect(() => {
    const move = (e: PointerEvent) => {
      const p = press.current;
      const m = motion.current;
      if (p && !now.current.drag) {
        const dist = Math.hypot(e.clientX - p.x, e.clientY - p.y);
        // Touch waits for a hold, so a swipe still scrolls the board; a mouse lifts once it moves.
        if (e.pointerType === 'touch') { if (dist > 8) { clearTimeout(p.timer); press.current = null; } return; }
        if (dist > 4) { press.current = null; fn.current.lift(p.id, p.el, e.clientX, e.clientY); }
        return;
      }
      if (now.current.drag?.mode !== 'pointer' || m.landing) return;
      const t = performance.now(), dt = Math.max(1, t - m.t) / 1000;
      m.vx = 0.7 * m.vx + 0.3 * ((e.clientX - m.x) / dt);
      m.vy = 0.7 * m.vy + 0.3 * ((e.clientY - m.y) / dt);
      Object.assign(m, { x: e.clientX, y: e.clientY, t });
    };
    const up = () => {
      if (press.current) { clearTimeout(press.current.timer); press.current = null; }
      if (now.current.drag?.mode === 'pointer' && !motion.current.landing) {
        // Settle the spot at the release point, then land once the slot is drawn there.
        motion.current.landing = true;
        fn.current.hitTest(motion.current.x, motion.current.y);
        requestAnimationFrame(() => fn.current.land());
      }
      setTimeout(() => { justDragged.current = false; });
    };
    const cancel = () => {
      if (press.current) { clearTimeout(press.current.timer); press.current = null; }
      const d = now.current.drag;
      if (d?.mode === 'pointer' && !motion.current.landing) fn.current.finish(d, false);
    };
    // While a card is in the air a touch moves it, not the page.
    const hold = (e: TouchEvent) => { if (now.current.drag?.mode === 'pointer') e.preventDefault(); };
    addEventListener('pointermove', move);
    addEventListener('pointerup', up);
    addEventListener('pointercancel', cancel);
    addEventListener('touchmove', hold, { passive: false });
    return () => {
      removeEventListener('pointermove', move); removeEventListener('pointerup', up);
      removeEventListener('pointercancel', cancel); removeEventListener('touchmove', hold);
      cancelAnimationFrame(motion.current.frame);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function onPointerDown(e: React.PointerEvent) {
    const el = (e.target as HTMLElement).closest<HTMLElement>('[data-card]');
    if (drag?.mode === 'keyboard') { finish(drag, false); return; }
    if (!el || e.button !== 0 || drag) return;
    const card = cards.find((c) => c.id === el.dataset.card);
    if (!card || card.busy) return;
    const p = { id: card.id, x: e.clientX, y: e.clientY, el } as NonNullable<typeof press.current>;
    if (e.pointerType === 'touch') p.timer = window.setTimeout(() => { if (press.current === p) { press.current = null; fn.current.lift(p.id, el, p.x, p.y); } }, 300);
    press.current = p;
  }

  // ---- keyboard: Space lifts, arrows move, Space or Enter drops, Escape puts it back
  function onKeyDown(e: React.KeyboardEvent) {
    const el = (e.target as HTMLElement).closest<HTMLElement>('[data-card]');
    if (!el) return;
    if (drag?.mode === 'pointer' && e.key === 'Escape') { e.preventDefault(); finish(drag, false); return; }
    const card = cards.find((c) => c.id === el.dataset.card);
    if (!card) return;
    if (!drag) {
      if (e.key !== ' ') return;
      e.preventDefault();
      if (card.busy) { say(`${card.title} is still saving.`); return; }
      const from = spotOf(cards, card, lanes);
      put({ id: card.id, from, to: from, mode: 'keyboard', w: 0, h: 0 });
      say(`Picked up ${card.title}. ${where(cards, from)}. Arrow keys move it, Space drops it, Escape puts it back.`);
      return;
    }
    if (drag.id !== card.id) return;
    const to = { ...drag.to };
    const count = (s: { status: string; lane: string | null }) => cards.filter((c) => c.id !== drag.id && inGroup(c, s, lanes)).length;
    const cols = columns.filter((c) => !c.closed || c.id === drag.from.status).map((c) => c.id);
    const ci = cols.indexOf(to.status), li = laneIds.indexOf(to.lane);
    switch (e.key) {
      case ' ': case 'Enter': e.preventDefault(); finish(drag, true); return;
      case 'Escape': e.preventDefault(); finish(drag, false); return;
      case 'ArrowLeft': case 'ArrowRight': {
        const next = cols[ci + (e.key === 'ArrowLeft' ? -1 : 1)];
        if (!next) return;
        to.status = next; to.index = Math.min(to.index, count(to));
        break;
      }
      case 'ArrowUp':
        if (to.index > 0) to.index--;
        else if (lanes && li > 0) { to.lane = laneIds[li - 1]!; to.index = count(to); } else return;
        break;
      case 'ArrowDown':
        if (to.index < count(to)) to.index++;
        else if (lanes && li < laneIds.length - 1) { to.lane = laneIds[li + 1]!; to.index = 0; } else return;
        break;
      case 'Tab': finish(drag, false); return;
      default: return;
    }
    e.preventDefault();
    put({ ...drag, to });
    say(where(place(cards, drag.id, to, lanes), to));
  }

  function onClick(e: React.MouseEvent) {
    const el = (e.target as HTMLElement).closest<HTMLElement>('[data-card]');
    if (!el || justDragged.current || drag) return;
    setOpenId(el.dataset.card!);
  }

  function add(e: FormEvent) {
    e.preventDefault();
    const text = draft.trim();
    if (!text || !open[0]) return;
    const card: KanbanCard = { id: crypto.randomUUID(), title: text, status: open[0].id, assignee: currentUser ?? people[0]?.id ?? null };
    change([...cards, card]);
    setDraft('');
    say(`Added ${text} to ${open[0].title}.`);
  }

  const edit = (id: string, patch: Partial<KanbanCard>) => {
    const c = cards.find((x) => x.id === id);
    if (!c) return;
    if (patch.status !== undefined || patch.assignee !== undefined) {
      const to = { status: patch.status ?? c.status, lane: patch.assignee !== undefined ? patch.assignee : c.assignee, index: Infinity };
      change(place(cards, id, to, true).map((x) => (x.id === id ? { ...x, ...patch } : x)));
    } else change(cards.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  };

  // Count what the column holds as the board shows it now, plus what the server has that is not loaded.
  const counts = (status: string) => {
    const col = colOf.get(status)!;
    const n = shown.filter((c) => c.status === status).length;
    return n + (col.total !== undefined ? col.total - cards.filter((c) => c.status === status).length : 0);
  };
  const head = (col: BoardColumn) => {
    const n = counts(col.id);
    const over = col.limit !== undefined && n > col.limit ? n - col.limit : 0;
    const loaded = cards.filter((c) => c.status === col.id).length;
    return (
      <div className="board-col-h">
        <h3 id={`${uid}-${col.id}`}>{col.title}</h3>
        <span className="faint small">
          {col.limit !== undefined ? `${n} of ${col.limit}` : n}{col.note ? ` · ${col.note}` : ''}
          {over > 0 && <> · <strong className="board-over">Over the limit by {over}</strong></>}
        </span>
        {lanes && onLoadMore && col.total !== undefined && loaded < col.total && <Button type="button" variant="ghost" size="sm" onClick={() => onLoadMore(col.id)}>Show more ({col.total - loaded})</Button>}
      </div>
    );
  };
  const overLimit = (col: BoardColumn) => col.limit !== undefined && counts(col.id) > col.limit;

  const cell = (col: BoardColumn, lane: string | null) => {
    const items = shown.filter((c) => inGroup(c, { status: col.id, lane }, lanes));
    return (
      <ul className="board-cards" data-drop={col.id} data-lane={lane ?? ''} aria-label={lanes ? `${laneName(lane)}, ${col.title}` : undefined}>
        {items.map((c) => (
          <li key={c.id}>
            {drag?.id === c.id && drag.mode === 'pointer'
              ? <div className="board-slot" data-drop-slot style={{ height: drag.h }} />
              : <CardView card={c} who={c.assignee ? nameOf.get(c.assignee) ?? null : null} lifted={drag?.id === c.id} />}
          </li>
        ))}
      </ul>
    );
  };

  const dragged = drag?.mode === 'pointer' ? cards.find((c) => c.id === drag.id) : undefined;
  const openCard = openId ? cards.find((c) => c.id === openId) ?? null : null;
  return (
    <div className={`stack ${className ?? ''}`} style={{ gap: 10 }}>
      <div className="board-bar">
        <h2>{title}</h2>
        <ToggleGroup label="Group by" value={groupBy} options={[['status', names.status], ['assignee', names.assignee]]} onChange={(g) => { if (drag) finish(drag, false); setGroupBy(g); }} />
        {!fixed.includes('add') && open.length > 0 && (
          <form className="board-add" onSubmit={add}>
            <label htmlFor={`${uid}-add`} className="sr-only">New card title</label>
            <input id={`${uid}-add`} className={`${fieldClass} h-9`} placeholder={`Add to ${open[0]!.title}`} value={draft} onChange={(e) => setDraft(e.target.value)} />
            <Button type="submit" variant="secondary" size="sm" disabled={!draft.trim()}>Add</Button>
          </form>
        )}
      </div>
      {/* Focusable, so a keyboard can scroll the board when it is wider than the screen. */}
      <div ref={scroller} className={`board${drag ? ' dragging' : ''}`} role="group" aria-label={title} tabIndex={0}
        style={{ ['--n' as string]: columns.length }} onPointerDown={onPointerDown} onKeyDown={onKeyDown} onKeyUp={(e) => { if (e.key === ' ' && (e.target as HTMLElement).closest('[data-card]')) e.preventDefault(); }} onClick={onClick}>
        <div className="board-inner">
          {!lanes ? (
            <div className="board-row">
              {columns.map((col) => (
                <section key={col.id} className={`board-col${col.closed ? ' closed' : ''}${overLimit(col) ? ' over-limit' : ''}`} aria-labelledby={`${uid}-${col.id}`}>
                  {head(col)}
                  {cell(col, null)}
                  {onLoadMore && col.total !== undefined && cards.filter((c) => c.status === col.id).length < col.total && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => onLoadMore(col.id)}>Show more ({col.total - cards.filter((c) => c.status === col.id).length})</Button>
                  )}
                </section>
              ))}
            </div>
          ) : (
            <>
              <div className="board-row">
                {columns.map((col) => <div key={col.id} className={`board-col board-col-top${col.closed ? ' closed' : ''}${overLimit(col) ? ' over-limit' : ''}`}>{head(col)}</div>)}
              </div>
              {laneIds.map((lane) => (
                <section key={lane ?? ''} className="board-lane" aria-labelledby={`${uid}-lane-${lane ?? ''}`}>
                  <h3 id={`${uid}-lane-${lane ?? ''}`} className="board-lane-h">{laneName(lane)} <span className="faint small">{shown.filter((c) => c.assignee === lane).length}</span></h3>
                  <div className="board-row">{columns.map((col) => <div key={col.id} className={`board-col${col.closed ? ' closed' : ''}`}>{cell(col, lane)}</div>)}</div>
                </section>
              ))}
            </>
          )}
        </div>
        {dragged && <div ref={float} className="board-float" aria-hidden="true" style={{ width: drag!.w, height: drag!.h }}><CardBody copy card={dragged} who={dragged.assignee ? nameOf.get(dragged.assignee) ?? null : null} /></div>}
        <div ref={live} className="sr-only" aria-live="assertive" aria-atomic="true" />
      </div>
      <Drawer card={openCard} columns={columns} people={people} fixed={fixed} names={names} onEdit={edit} onClose={() => {
        const id = openId;
        setOpenId(null);
        setTimeout(() => scroller.current?.querySelector<HTMLElement>(`[data-card="${CSS.escape(id ?? '')}"]`)?.focus());
      }} />
    </div>
  );
}

function CardBody({ card, who, copy }: { card: KanbanCard; who: string | null; copy?: boolean }) {
  const done = card.checklist?.filter((i) => i.done).length ?? 0;
  return (
    <>
      <span className="item-title">{card.title}</span>
      <span id={copy ? undefined : `card-${card.id}-d`} className="board-card-d">
        {card.subtitle && <span className="item-sub">{card.subtitle}</span>}
        <span className="row small" style={{ gap: 6 }}>
          {card.meta && <strong>{card.meta}</strong>}
          {card.checklist?.length ? <span className="faint">{done} of {card.checklist.length} done</span> : null}
          {card.flags?.map((f) => <Badge key={f} tone="warning">{f}</Badge>)}
          {card.problem && <Badge tone="danger">Needs details</Badge>}
          {who && <span className="avatar sm board-who" title={who} aria-hidden="true">{initials(who)}</span>}
        </span>
      </span>
    </>
  );
}

// Memoized: a card re-renders only when it, its assignee's name, or its lift changes.
const CardView = memo(function CardView({ card, who, lifted }: { card: KanbanCard; who: string | null; lifted: boolean }) {
  return (
    <button type="button" className={`board-card${lifted ? ' lifted' : ''}${card.busy ? ' busy' : ''}`} data-card={card.id}
      aria-roledescription="draggable card" aria-label={`${card.title}, ${who ?? 'unassigned'}`} aria-describedby={`card-${card.id}-d`}
      aria-busy={card.busy || undefined}>
      <CardBody card={card} who={who} />
    </button>
  );
});

// The card's detail drawer (the library's sheet): focus moves in, Tab stays inside, and
// Escape or its close button closes it. The board puts focus back on the card.
function Drawer({ card, columns, people, fixed, names, onEdit, onClose }: {
  card: KanbanCard | null; columns: BoardColumn[]; people: BoardPerson[]; fixed: Fixed[]; names: { status: string; assignee: string };
  onEdit: (id: string, patch: Partial<KanbanCard>) => void; onClose: () => void;
}) {
  const body = useRef<HTMLDivElement>(null);
  // A refusal puts focus on the first field it needs, once the card is saved and its fields take input again.
  useEffect(() => {
    if (card?.problem && !card.busy) body.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
  }, [card?.id, card?.problem?.message, card?.busy]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Sheet open={!!card} onOpenChange={(open) => { if (!open) onClose(); }}>
      {card && (
        <SheetContent title={card.title} className="w-[min(28rem,100vw)]">
          <div ref={body}><DrawerBody key={card.id} card={card} columns={columns} people={people} fixed={fixed} names={names} onEdit={onEdit} /></div>
        </SheetContent>
      )}
    </Sheet>
  );
}

function DrawerBody({ card, columns, people, fixed, names, onEdit }: {
  card: KanbanCard; columns: BoardColumn[]; people: BoardPerson[]; fixed: Fixed[]; names: { status: string; assignee: string };
  onEdit: (id: string, patch: Partial<KanbanCard>) => void;
}) {
  const [title, setTitle] = useState(card.title);
  const [description, setDescription] = useState(card.description ?? '');
  const id = useId();
  const saveTitle = () => { const t = title.trim(); if (t && t !== card.title) onEdit(card.id, { title: t }); else setTitle(card.title); };
  return (
    <div className="stack" style={{ gap: 14 }}>
      {card.subtitle && <p className="item-sub" style={{ margin: 0 }}>{card.subtitle}{card.meta ? ` · ${card.meta}` : ''}</p>}
      {card.problem && <Alert tone="danger" role="alert">{card.problem.message}</Alert>}
      {!fixed.includes('title') && (
        <div className="field"><label htmlFor={`${id}-t`}>Title</label>
          <input id={`${id}-t`} className={fieldClass} value={title} disabled={card.busy} onChange={(e) => setTitle(e.target.value)} onBlur={saveTitle} onKeyDown={(e) => { if (e.key === 'Enter') saveTitle(); }} /></div>
      )}
      <div className="field"><label htmlFor={`${id}-s`}>{names.status}</label>
        <select id={`${id}-s`} className={selectClass} value={card.status} disabled={card.busy} aria-busy={card.busy || undefined} onChange={(e) => onEdit(card.id, { status: e.target.value })}>
          {columns.filter((c) => !c.closed || c.id === card.status).map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
        </select></div>
      {!fixed.includes('assignee') && (
        <div className="field"><label htmlFor={`${id}-a`}>{names.assignee}</label>
          <select id={`${id}-a`} className={selectClass} value={card.assignee ?? ''} disabled={card.busy} onChange={(e) => onEdit(card.id, { assignee: e.target.value || null })}>
            <option value="">Unassigned</option>
            {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select></div>
      )}
      {!fixed.includes('description') && (
        <div className="field"><label htmlFor={`${id}-d`}>Description</label>
          <textarea id={`${id}-d`} className={textareaClass} rows={4} value={description} disabled={card.busy} onChange={(e) => setDescription(e.target.value)}
            onBlur={() => { if (description !== (card.description ?? '')) onEdit(card.id, { description }); }} /></div>
      )}
      {card.fields?.map((f) => (
        // Keyed by the saved value, so a reload after a save starts the input from what was saved.
        <CardFieldInput key={`${f.key}:${f.value}`} field={f} busy={card.busy} needed={card.problem?.fields.includes(f.key) ?? false}
          onSave={(value) => onEdit(card.id, { fields: card.fields!.map((x) => (x.key === f.key ? { ...x, value } : x)) })} />
      ))}
      {card.checklist?.length ? (
        <fieldset className="board-check">
          <legend>Checklist, {card.checklist.filter((i) => i.done).length} of {card.checklist.length} done</legend>
          {card.checklist.map((item) => (
            <label key={item.id} className="row" style={{ gap: 8 }}>
              <input type="checkbox" checked={item.done} disabled={card.busy}
                onChange={() => onEdit(card.id, { checklist: card.checklist!.map((i) => (i.id === item.id ? { ...i, done: !i.done } : i)) })} />
              {item.text}
            </label>
          ))}
        </fieldset>
      ) : null}
      {card.flags?.length ? <div className="row" style={{ gap: 6 }}>{card.flags.map((f) => <Badge key={f} tone="warning">{f}</Badge>)}</div> : null}
      {card.href && <Link to={card.href}>Open {card.title}</Link>}
    </div>
  );
}

function CardFieldInput({ field, busy, needed, onSave }: { field: CardField; busy?: boolean; needed: boolean; onSave: (value: string) => void }) {
  const [value, setValue] = useState(field.value);
  const save = () => { if (value.trim() !== field.value) onSave(value.trim()); };
  return (
    <Field label={field.label} required={needed} error={needed && !value.trim() ? 'Needed to move it' : null}>
      <input className={fieldClass} type={field.type} inputMode={field.type === 'number' ? 'decimal' : undefined} step={field.type === 'number' ? 'any' : undefined}
        // A date is whole once picked, so it saves then; text and numbers save on Enter or leaving the field.
        value={value} disabled={busy} onChange={(e) => { setValue(e.target.value); if (field.type === 'date' && e.target.value && e.target.value !== field.value) onSave(e.target.value); }} onBlur={save} onKeyDown={(e) => { if (e.key === 'Enter') save(); }} />
    </Field>
  );
}
